import { Fragment, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { api } from "../../api/client";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { useAuth } from "../auth/useAuth";
import { hhmm, taipeiToday } from "./time";
import { formatLeaveDays } from "../leave/LeaveBalance";
import { clockRange } from "../leave/LeaveCalendar";
import { isHoliday, useTwHolidays } from "../../lib/twHolidays";
import { missingPunches, parseClock, spokenClock, type MissingPunch } from "./backfill";
import type { AttendanceMonth, AttendanceSummary, UserPick } from "../../api/types";

// 出勤紀錄(業主 2026-09-21)。
//
// > 每個人左側都要顯示自己的上下班打卡紀錄 請假紀錄(這功能叫做出勤紀錄)
// > 如果那一天請假他顯示的就是請假類別 要有月表
//
// ==========================================================================
// 一列一天,**當月每一天都列**
// ==========================================================================
// 週末與一次都沒打卡的日子也要有一列。不列的話看不出缺漏 —— 而「哪一天忘了
// 打卡」正是這張表最常被拿來回答的問題。
//
// ==========================================================================
// 刪除那顆鈕是從首頁搬過來的
// ==========================================================================
// 首頁那個小工具原本在底下列出今天每一筆打卡,而每一列有「刪除」。業主
// 2026-09-21 說那張清單「太長了」要拿掉 —— **但那是目前唯一刪得掉打錯那一筆
// 的入口**,所以它搬到這裡的展開列裡,不是跟著清單一起消失。

/** `2026-09-01` → `09/01`。認不得就原樣回去,不猜。 */
function mmdd(iso: string): string {
  const parts = iso.split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : iso;
}

/** 1=一 …… 7=日。後端給的是 `isoweekday`。 */
const WEEKDAY = ["", "一", "二", "三", "四", "五", "六", "日"];

/** 半天假要看得出是哪半天 —— 「特休」與「特休 上午」是兩件事。 */
const PERIOD: Record<string, string> = { FULL: "", AM: "上午", PM: "下午" };

function leaveText(leave: AttendanceMonth["days"][number]["leave"]): string {
  if (!leave) return "";
  // 按小時的假(業主 2026-11-03)寫幾點到幾點:「事假 09:00–11:00」;舊單寫「事假 2 小時」
  const half =
    leave.hours != null
      ? (clockRange(leave.start_hour, leave.hours) ?? `${leave.hours} 小時`)
      : (PERIOD[leave.period] ?? "");
  // **待核准要標出來** —— 還沒准的假不該看起來像已經准了。
  const pending = leave.status === "PENDING" ? "(待核准)" : "";
  return `${leave.label}${half ? ` ${half}` : ""}${pending}`;
}

const HEADERS = ["日期", "星期", "上班", "下班", "工時", "狀態"];

/**
 * 補登(業主 2026-09-25:首頁的打卡小工具拔掉了,「出勤紀錄頁裡自己補」)。
 *
 * **只有補登,沒有「現在打卡」** —— 即時打卡只在門口的打卡機上。
 * 後端也擋著:這裡送的一定帶時間,``is_manual`` 一律為真,總表上看得出是補的。
 *
 * 使用者填的是台北的牆上時間,送出時帶上 `+08:00` —— 不帶的話會被當成 UTC,
 * 早上九點的補登會變成下午五點。
 */
function BackfillForm({ onDone, missing }: { onDone: () => void; missing: MissingPunch[] }) {
  const toast = useToast();
  const [onDate, setOnDate] = useState(taipeiToday());
  const [atTime, setAtTime] = useState("09:00");
  const [kind, setKind] = useState<"IN" | "OUT">("IN");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    if (onDate === "" || atTime.trim() === "") {
      setError("日期與時間都要填");
      return;
    }
    const clock = parseClock(atTime);
    if (clock === null) {
      setError("時間請用 24 小時制,例如 09:30、17:30");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.post("/attendance/punch", {
        kind,
        punched_at: `${onDate}T${clock}:00+08:00`,
        note: note.trim(),
      });
      toast.push("補登好了", "success");
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "補不進去");
    } finally {
      setBusy(false);
    }
  };

  const parsed = parseClock(atTime);
  const pick = (one: MissingPunch) => {
    setOnDate(one.date);
    setKind(one.kind);
    setAtTime(one.kind === "IN" ? "09:00" : "18:00");
    setError(null);
  };

  return (
    <form
      aria-label="補登打卡"
      onSubmit={submit}
      className="flex flex-wrap items-end gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3"
    >
      {missing.length > 0 ? (
        <div className="flex w-full flex-col gap-1">
          <p className="m-0 text-sm text-[var(--text-2)]">這個月還缺這幾張卡,點一下就帶進下面:</p>
          <ul aria-label="還缺的卡" className="m-0 flex list-none flex-wrap gap-1 p-0">
            {missing.map((one) => (
              <li key={`${one.date}-${one.kind}`}>
                <button
                  type="button"
                  onClick={() => pick(one)}
                  className={
                    "rounded-[var(--radius)] border px-2 py-0.5 text-xs " +
                    (onDate === one.date && kind === one.kind
                      ? "border-[var(--accent)] bg-[var(--surface-2)] text-[var(--text)]"
                      : "border-[var(--border-strong)] text-[var(--text-2)] hover:bg-[var(--surface-2)]")
                  }
                >
                  {`${mmdd(one.date)}(${"日一二三四五六"[new Date(`${one.date}T00:00:00Z`).getUTCDay()]}) ${one.kind === "IN" ? "上班" : "下班"}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <Input label="日期" type="date" value={onDate} onChange={(e) => setOnDate(e.target.value)} />
      {/* 24 小時制、打字輸入(業主 2026-11-02):瀏覽器原生的時間欄有上午/下午,
          打「5」會存成上午 05:00。旁邊把它念出來,填錯一眼看得到。 */}
      {/* 念出來的字放在標籤那一行,不放欄位底下 —— 放底下會把這一格頂高,跟旁邊對不齊。 */}
      <Input
        label={
          <>
            時間
            <span className="ml-2 whitespace-nowrap font-normal text-[var(--text-3)]">
              {parsed ? spokenClock(parsed) : "24 小時制"}
            </span>
          </>
        }
        inputMode="numeric"
        placeholder="例 17:30"
        value={atTime}
        onChange={(e) => setAtTime(e.target.value)}
        className="w-28"
      />
      <Select
        label="上班或下班"
        value={kind}
        options={[
          { value: "IN", label: "上班" },
          { value: "OUT", label: "下班" },
        ]}
        onChange={(e) => setKind(e.target.value as "IN" | "OUT")}
      />
      <Input label="備註" value={note} placeholder="例:忘了打" onChange={(e) => setNote(e.target.value)} />
      <Button type="submit" variant="primary" loading={busy}>
        送出補登
      </Button>
      {error ? (
        <p role="alert" className="m-0 w-full text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
    </form>
  );
}

export function AttendancePage() {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  // 這一頁現在在看誰。`null` = 自己,數字 = 某個人,`"ALL"` = 全員總表
  // (業主 2026-09-23:「要有一個簡易總表 一次看到所有人」)。
  //
  // **不另開一頁**:那個下拉本來就是「現在在看誰」,「全員」是它的另一個答案。
  // 另開一頁的話同一件事要記兩個入口,換月份那幾顆鈕還要複製一份。
  const [who, setWho] = useState<number | null | "ALL">(null);
  const [open, setOpen] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);

  const isOwner = user?.role === "OWNER";

  // **人員下拉只有業主有。** 出勤與請假是個人資料 —— 誰幾點上下班、誰請了
  // 病假,不是同仁之間該互相看得到的事(後端那一支也擋著,回 403)。
  const people = useQuery({
    queryKey: ["users", "pickable"],
    queryFn: () => api.get<UserPick[]>("/users/pickable"),
    enabled: isOwner,
  });

  const everyone = who === "ALL";

  const sheet = useQuery({
    queryKey: ["attendance-month", year, month, who],
    queryFn: () =>
      api.get<AttendanceMonth>("/attendance/month", {
        year,
        month,
        ...(typeof who === "number" ? { user_id: who } : {}),
      }),
    enabled: !everyone,
  });

  // 總表只有業主叫得動(後端也擋著,回 403)。`enabled` 少了 isOwner 的話,
  // 同仁的頁面會在背景打一支必定 403 的請求 —— 看不出症狀,但那是在問一件
  // 自己沒有權限知道的事。
  const summary = useQuery({
    queryKey: ["attendance-summary", year, month],
    queryFn: () => api.get<AttendanceSummary>("/attendance/summary", { year, month }),
    enabled: isOwner && everyone,
  });

  const remove = useMutation({
    mutationFn: (punchId: number) => api.del(`/attendance/punch/${punchId}`),
    onSuccess: () => {
      toast.push("已丟進回收桶,七天內救得回來", "success");
      void queryClient.invalidateQueries({ queryKey: ["attendance-month"] });
      // 首頁那個時鐘讀的是同一天的資料,一起重查。
      void queryClient.invalidateQueries({ queryKey: ["attendance-today"] });
    },
    onError: (err: unknown) => {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    },
  });

  const step = (by: number) => {
    const at = new Date(year, month - 1 + by, 1);
    setYear(at.getFullYear());
    setMonth(at.getMonth() + 1);
    setOpen(null);
  };

  const days = sheet.data?.days ?? [];
  // 還缺哪幾張卡:只看自己的月表(補登只補得了自己的)。
  const holidays = useTwHolidays([year]);
  const missing = who === null ? missingPunches(days, taipeiToday(), (iso) => isHoliday(holidays, iso)) : [];

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">出勤紀錄</h2>
        <Button
          icon={<Plus aria-hidden="true" size={16} />}
          aria-expanded={backfilling}
          onClick={() => setBackfilling((now) => !now)}
        >
          {missing.length > 0 ? `補登(缺 ${missing.length} 張)` : "補登"}
        </Button>
      </div>
      <p className="m-0 text-sm text-[var(--text-2)]">
        上下班請到門口的打卡機打卡;忘了打的,按「補登」自己補,紀錄上會標成補登。
      </p>
      {backfilling ? (
        <BackfillForm
          missing={missing}
          onDone={() => {
            setBackfilling(false);
            void queryClient.invalidateQueries({ queryKey: ["attendance-month"] });
          }}
        />
      ) : null}
      <p className="m-0 text-sm text-[var(--text-2)]">
        一列一天。請假那一天照樣看得到打卡時間 —— 半天假本來就是一半上班。
        點日期展開,可以刪掉打錯的那一筆。
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            aria-label="上一個月"
            icon={<ChevronLeft aria-hidden="true" size={16} />}
            onClick={() => step(-1)}
          >
            上一個月
          </Button>
          <span className="tabular px-2 text-sm font-medium text-[var(--text)]">
            {year} 年 {month} 月
          </span>
          <Button
            type="button"
            aria-label="下一個月"
            icon={<ChevronRight aria-hidden="true" size={16} />}
            onClick={() => step(1)}
          >
            下一個月
          </Button>
        </div>

        {isOwner ? (
          <Select
            label="看誰的"
            className="w-40"
            value={who === null ? "" : String(who)}
            onChange={(e) => {
              const picked = e.target.value;
              setWho(picked === "" ? null : picked === "ALL" ? "ALL" : Number(picked));
              setOpen(null);
            }}
            options={[
              { value: "", label: "我自己" },
              { value: "ALL", label: "全員" },
              ...(people.data ?? []).map((one) => ({
                value: String(one.id),
                label: one.name || one.email,
              })),
            ]}
          />
        ) : null}
      </div>

      {(everyone ? summary.isLoading : sheet.isLoading) ? (
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      ) : null}
      {(everyone ? summary.error : sheet.error) ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          出勤紀錄載入失敗,請稍後再試。
        </p>
      ) : null}

      {/* 全員總表。一個人一列,**逐日明細不在這裡** —— 點名字才是那個人的月表。
          總表回答「誰有問題」,月表回答「哪一天有問題」。 */}
      {everyone && summary.data ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="全員出勤總表" className="w-full min-w-[30rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                <th scope="col" className="whitespace-nowrap px-3 py-2">姓名</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2 text-right">出勤天數</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2 text-right">總工時</th>
                <th scope="col" className="whitespace-nowrap px-3 py-2 text-right">請假天數</th>
              </tr>
            </thead>
            <tbody>
              {summary.data.rows.map((one) => (
                <tr key={one.user_id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setWho(one.user_id);
                        setOpen(null);
                      }}
                      className="bg-transparent p-0 text-left text-[var(--accent)] underline-offset-2 hover:underline"
                    >
                      {one.name}
                    </button>
                    {/* **離職的人標出來,但數字照樣在** —— 九月做到一半離職的人,
                        九月的工時還是要領薪水的。 */}
                    {one.active ? null : (
                      <span className="ml-2 rounded-[var(--radius)] bg-[var(--surface-2)] px-1.5 py-0.5 text-xs text-[var(--text-3)]">
                        已停用
                      </span>
                    )}
                  </td>
                  <td className="tabular px-3 py-2 text-right text-sm text-[var(--text)]">{one.worked_days}</td>
                  <td className="tabular px-3 py-2 text-right text-sm text-[var(--text)]">{one.total_hours}</td>
                  <td className="tabular px-3 py-2 text-right text-sm text-[var(--text-2)]">{formatLeaveDays(one.leave_days)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--border-strong)] bg-[var(--surface-2)]">
                <td className="px-3 py-2 text-sm font-medium text-[var(--text-2)]">全公司</td>
                <td className="tabular px-3 py-2 text-right text-sm font-semibold text-[var(--text)]">
                  {summary.data.worked_days}
                </td>
                <td className="tabular px-3 py-2 text-right text-sm font-semibold text-[var(--text)]">
                  {summary.data.total_hours}
                </td>
                <td className="tabular px-3 py-2 text-right text-sm font-semibold text-[var(--text)]">
                  {formatLeaveDays(summary.data.leave_days)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}

      {!everyone && days.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="出勤月表" className="w-full min-w-[36rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {HEADERS.map((one) => (
                  <th key={one} scope="col" className="whitespace-nowrap px-3 py-2">
                    {one}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((one) => {
                const weekend = one.weekday >= 6;
                const expanded = open === one.work_date;
                return (
                  <Fragment key={one.work_date}>
                    <tr
                      className={
                        "border-t border-[var(--border)] " +
                        (weekend ? "bg-[var(--surface-2)]" : "")
                      }
                    >
                      <td className="px-3 py-2 text-sm">
                        {/* 日期本身就是展開鈕 —— 一列只有一個點擊目標,
                            不必先找那個小箭頭在哪。 */}
                        <button
                          type="button"
                          aria-expanded={expanded}
                          aria-label={`${mmdd(one.work_date)} 的打卡`}
                          onClick={() => setOpen(expanded ? null : one.work_date)}
                          className="tabular flex items-center gap-1 rounded-[var(--radius)] text-[var(--accent)] underline-offset-2 hover:underline"
                        >
                          {mmdd(one.work_date)}
                          <ChevronDown
                            aria-hidden="true"
                            size={14}
                            className={expanded ? "" : "-rotate-90"}
                          />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-sm text-[var(--text-2)]">
                        {WEEKDAY[one.weekday] ?? ""}
                      </td>
                      <td className="tabular px-3 py-2 text-sm text-[var(--text)]">
                        {hhmm(one.first_in) || "—"}
                        {one.manual_count > 0 ? (
                          <span className="ml-1 text-xs text-[var(--warn)]">補</span>
                        ) : null}
                      </td>
                      <td className="tabular px-3 py-2 text-sm text-[var(--text)]">
                        {one.working ? "上班中" : hhmm(one.last_out) || "—"}
                      </td>
                      <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">
                        {one.punch_count > 0 ? one.hours : ""}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {one.leave ? (
                          <Badge tone={one.leave.status === "PENDING" ? "warn" : "accent"}>
                            {leaveText(one.leave)}
                          </Badge>
                        ) : null}
                      </td>
                    </tr>

                    {expanded ? (
                      <tr className="border-t border-[var(--border)]">
                        <td colSpan={HEADERS.length} className="px-3 py-2">
                          {one.punches.length === 0 ? (
                            <p className="m-0 text-sm text-[var(--text-3)]">
                              這一天沒有打卡紀錄。
                            </p>
                          ) : (
                            <ul className="m-0 flex list-none flex-col gap-1 p-0">
                              {one.punches.map((punch) => (
                                <li
                                  key={punch.id}
                                  className="flex flex-wrap items-center gap-2 text-sm"
                                >
                                  <Badge tone="neutral">
                                    {punch.kind === "IN" ? "上班" : "下班"}
                                  </Badge>
                                  <span className="tabular text-[var(--text)]">
                                    {hhmm(punch.punched_at)}
                                  </span>
                                  {punch.is_manual ? <Badge tone="warn">補登</Badge> : null}
                                  <span className="min-w-0 flex-1 truncate text-xs text-[var(--text-3)]">
                                    {punch.note}
                                  </span>
                                  {/* **只刪得掉自己的**(後端擋著,老闆也不行)——
                                      別人的出勤被改掉,被改的那個人完全看不出來。 */}
                                  {who === null ? (
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      aria-label={`刪除 ${hhmm(punch.punched_at)} 那一筆`}
                                      icon={<Trash2 aria-hidden="true" size={14} />}
                                      onClick={() => {
                                        const ok = window.confirm(
                                          `要刪掉 ${mmdd(one.work_date)} ` +
                                            `${hhmm(punch.punched_at)} 的` +
                                            `${punch.kind === "IN" ? "上班" : "下班"}卡嗎?\n\n` +
                                            "會放進回收桶七天,期間隨時還原得回來。",
                                        );
                                        if (ok) remove.mutate(punch.id);
                                      }}
                                    >
                                      刪除
                                    </Button>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {sheet.data ? (
        <p className="m-0 text-sm text-[var(--text-2)]">
          這個月出勤{" "}
          <strong className="tabular">{sheet.data.worked_days}</strong> 天 · 總工時{" "}
          <strong className="tabular">{sheet.data.total_hours}</strong> 小時 · 請假{" "}
          <strong className="tabular">{formatLeaveDays(sheet.data.leave_days)}</strong> 天
        </p>
      ) : null}
    </div>
  );
}
